import React, { memo } from 'react';
import classNames from 'classnames';
import styles from './ActivityItem.module.scss';

interface ActivityItemProps {
  item: any;
  type: 'approvals' | 'invites' | 'review' | 'signing' | 'out_for_signing';
  onClick: () => void;
}

const ActivityItem: React.FC<ActivityItemProps> = memo(
  ({ item, type, onClick }) => {
    const renderContent = () => {
      switch (type) {
        case 'approvals':
          return (
            <>
              <div className={styles.itemTitle}>{item.title || 'Untitled Document'}</div>
              {item.deal && <div className={styles.itemSubtitle}>Deal: {item.deal.title || 'Untitled Deal'}</div>}
            </>
          );

        case 'invites':
          return (
            <>
              <div className={styles.itemTitle}>{item.title || 'Deal Invitation'}</div>
              {item.invitationSentBy && (
                <div className={styles.itemSubtitle}>From: {item.invitationSentBy.fullName}</div>
              )}
            </>
          );

        case 'review':
          return (
            <>
              <div className={styles.itemTitle} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.title || 'Untitled Document'}
                </span>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '500',
                    flexShrink: 0,
                    backgroundColor: item.status === 'reviewing_external' ? '#ff9800' : '#29b6f6',
                    color: 'white',
                  }}
                >
                  {item.status === 'reviewing_external' ? 'External' : 'Internal'}
                </span>
              </div>
              {item.deal && <div className={styles.itemSubtitle}>Deal: {item.deal.title || 'Untitled Deal'}</div>}
            </>
          );

        case 'signing':
          return (
            <>
              <div className={styles.itemTitle}>{item.title || 'Untitled Document'}</div>
              {item.deal && <div className={styles.itemSubtitle}>Deal: {item.deal.title || 'Untitled Deal'}</div>}
            </>
          );

        case 'out_for_signing':
          return (
            <>
              <div className={styles.itemTitle}>{item.title || 'Untitled Document'}</div>
              {item.deal && <div className={styles.itemSubtitle}>Deal: {item.deal.title || 'Untitled Deal'}</div>}
              {item.signers && item.signers.length > 0 && (
                <div className={styles.itemSubtitle}>
                  Signers: {item.signers.map((signer: any) => signer.email).join(', ')}
                </div>
              )}
            </>
          );

        default:
          return null;
      }
    };

    return (
      <div
        className={classNames(styles.activityItem, styles[`activityItem--${type}`])}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
      >
        <div className={styles.itemContent}>{renderContent()}</div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for memo
    // Only re-render if item content or type changes
    return (
      prevProps.type === nextProps.type &&
      prevProps.item?.id === nextProps.item?.id &&
      prevProps.item?.title === nextProps.item?.title &&
      prevProps.item?.status === nextProps.item?.status &&
      prevProps.onClick === nextProps.onClick
    );
  }
);

ActivityItem.displayName = 'ActivityItem';

export default ActivityItem;
